import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TWO_MINUTES_IN_MILLIS } from '@core/consts';
import { SidenavService } from '@core/services/sidenav.service';
import { endOfDay, mapId, mapIds, startOfDay } from '@core/utils/filters-utils';
import { environment } from '@environment';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { DateIntervalValidation } from '@validators/date-interval-validation';
import { DatePeriodValidation } from '@validators/date-period-validation';
import { DateValidation } from '@validators/date.validation';
import { isEqual } from 'lodash';
import * as moment from 'moment';
import { Subject } from 'rxjs';
import { debounceTime, filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { sentry } from 'sentry';
import { ReportsService } from '../reports.service';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';
import { MaintenancesByStatus } from './services/dashboard-maintenances.service';


@Component({
  templateUrl: './dashboard-maintenances.component.html',
  styleUrls: ['./dashboard-maintenances.component.scss'],
})
export class DashboardMaintenancesComponent implements OnInit, OnDestroy {

  public isLoading = true;
  public isFilterOn = false;
  public totalMaintenances = 0;
  public isFilterExpanded = false;
  public accountMaintenancesStatusInterval = {
    responseAverage: '',
    solutionAverage: '',
    pending: '',
    scheduled: '',
    inProgress: '',
    reported: '',
    requested: '',
    onNegotiation: '',
  };
  public accountMaintenancesVolumeCount: any;
  public status: MaintenancesByStatus = {
    new: 0,
    pending: 0,
    requested: 0,
    onNegotiation: 0,
    scheduled: 0,
    inProgress: 0,
    done: 0,
    reported: 0,
    canceled: 0,
  };

  private intervalId: any;
  private subscriptions = new Subject<void>();

  private defaultFilterValue = {
    openedAtGte: moment().subtract(1, 'month'),
    openedAtLte: moment(),
    locations: null,
    maintenanceTypes: null,
    segments: null,
    companies: null,
    locationGroups: null,
    scheduledAtGte: null,
    scheduledAtLte: null,
    closedAtGte: null,
    closedAtLte: null,
  };

  public filterForm = new FormGroup({
    openedAtGte: new FormControl(this.defaultFilterValue.openedAtGte, [Validators.required, DateValidation.validate]),
    openedAtLte: new FormControl(this.defaultFilterValue.openedAtLte, [Validators.required, DateValidation.validate]),
    locations: new FormControl(this.defaultFilterValue.locations),
    maintenanceTypes: new FormControl(this.defaultFilterValue.maintenanceTypes),
    segments: new FormControl(this.defaultFilterValue.segments),
    companies: new FormControl(this.defaultFilterValue.companies),
    locationGroups: new FormControl(this.defaultFilterValue.locationGroups),
    scheduledAtGte: new FormControl(this.defaultFilterValue.scheduledAtGte, DateValidation.validate),
    scheduledAtLte: new FormControl(this.defaultFilterValue.scheduledAtLte, DateValidation.validate),
    closedAtGte: new FormControl(this.defaultFilterValue.closedAtGte, DateValidation.validate),
    closedAtLte: new FormControl(this.defaultFilterValue.closedAtLte, DateValidation.validate)
  }, {
    validators: [
      DateIntervalValidation.validate({
        from: 'openedAtGte',
        to: 'openedAtLte',
        maxDiffLte: 1,
        unit: 'year'
      }),
      DatePeriodValidation.validate(['openedAt', 'closedAt', 'scheduledAt'])
    ]
  });

  constructor(
    public sidenavService: SidenavService,
    private fuseTranslationLoaderService: FuseTranslationLoaderService,
    private router: Router,
    private fuseProgressBarService: FuseProgressBarService,
    private reportsService: ReportsService
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
  }

  ngOnInit(): void {
    this.updateAutoRefreshState({ enabled: false });

    this.filterForm.valueChanges.pipe(
      takeUntil(this.subscriptions),
      filter(() => this.filterForm.valid),
      debounceTime(200),
      tap(() => this.showLoading()),
      tap(() => {
        const currentWhere = this.toQuery(this.filterForm.value);
        const defaultWhere = this.toQuery(this.defaultFilterValue);
        this.isFilterOn = !isEqual(currentWhere, defaultWhere);
      }),
      switchMap(() => this.updateData()),
      tap(() => this.hideLoading())
    ).subscribe();

    this.reload();
  }

  ngOnDestroy(): void {
    this.subscriptions.next();
    this.subscriptions.complete();
    clearInterval(this.intervalId);
  }

  private toQuery(value: any): any {
    return {
      locationGroupId_in: mapIds(value.locationGroups),
      locationId_in: mapIds(value.locations),
      typeId_in: mapIds(value.maintenanceTypes),
      segmentId_in: mapIds(value.segments),
      companyId_in: mapIds(value.companies),
      from: startOfDay(value.openedAtGte),
      to: endOfDay(value.openedAtLte),
      scheduledAt_gte: startOfDay(value.scheduledAtGte, 'YYYY-MM-DD'),
      scheduledAt_lte: endOfDay(value.scheduledAtLte, 'YYYY-MM-DD'),
      closedAt_gte: startOfDay(value.closedAtGte),
      closedAt_lte: endOfDay(value.closedAtLte),
    };
  }

  private async updateData(): Promise<void> {
    try {
      const where = this.toQuery(this.filterForm.value);

      const [
        accountMaintenancesStatus,
        accountMaintenancesStatusInterval,
        accountMaintenancesVolumeCount
      ] = await Promise.all([
        this.reportsService.accountMaintenancesStatus(where),
        this.reportsService.accountMaintenancesStatusInterval(where),
        this.reportsService.accountMaintenancesVolumeCount(where)
      ]);

      this.status = accountMaintenancesStatus.status;
      this.totalMaintenances = accountMaintenancesStatus.totalMaintenances;
      this.accountMaintenancesStatusInterval = accountMaintenancesStatusInterval;
      this.accountMaintenancesVolumeCount = accountMaintenancesVolumeCount;
    } catch (err) {
      sentry.captureException(err);
    }
  }

  private buildQueryParams(): any {
    const query = this.toQuery(this.filterForm.value);
    return {
      locationGroupId_in: query.locationGroupId_in && query.locationGroupId_in.join(','),
      locationId_in: query.locationId_in && query.locationId_in.join(','),
      typeId_in: query.typeId_in && query.typeId_in.join(','),
      segmentId_in: query.segmentId_in && query.segmentId_in.join(','),
      companyId_in: query.companyId_in && query.companyId_in.join(','),
      openedAt_gte: startOfDay(query.from),
      openedAt_lte: endOfDay(query.to),
      scheduledAt_gte: startOfDay(query.scheduledAt_gte, 'YYYY-MM-DD'),
      scheduledAt_lte: endOfDay(query.scheduledAt_lte, 'YYYY-MM-DD'),
      closedAt_gte: startOfDay(query.closedAt_gte),
      closedAt_lte: endOfDay(query.closedAt_lte),
      archived: 'false'
    };
  }

  goToMaintenances(status?: string[], includeStatus: boolean = true): void {
    const queryParams = this.buildQueryParams();
    if (status) {
      if (includeStatus) {
        queryParams.status_in = status.join(',');
      } else {
        queryParams.status_not_in = status.join(',');
      }
    }
    const url = this.router.serializeUrl(this.router.createUrlTree(['manutencoes'], { queryParams: { ...queryParams } }));

    const absoluteUrl = `${environment.MOUNTDOOM_URL}/#${url}`;

    try {
      window.open(absoluteUrl);
    } catch (error) {
      console.log('erro: 657aeae2-bc2f-4a2b-8eef-1181405b8b7b');
      console.error(error);
      sentry.captureException(error);
    }
  }

  updateAutoRefreshState({ enabled }: { enabled: boolean }): void {
    if (enabled) {
      this.intervalId = setInterval(() => this.reload(), TWO_MINUTES_IN_MILLIS);
    } else {
      clearInterval(this.intervalId);
    }
  }

  clearFilters(): void {
    this.filterForm.setValue(this.defaultFilterValue);
  }

  private showLoading(): void {
    this.isLoading = true;
    this.fuseProgressBarService.show();
  }

  private hideLoading(): void {
    this.isLoading = false;
    this.fuseProgressBarService.hide();
  }

  public reload(): void {
    this.filterForm.updateValueAndValidity();
  }

  @HostListener('window:beforeprint')
  async beforePrint(): Promise<void> {
    this.isFilterExpanded = true;
  }
}
